/**
 * CompareTable — renders the server-built `compare_table` artifact from
 * the `compare_cells_table` tool.
 *
 * Metrics are rows, cells are columns. Every value is already verified
 * server-side (each carries its tool_call_id), so we render each as a
 * DataValueChip — the AI never typed a number or a handle. Works for a
 * single cell (a spec sheet) or 2-8 cells (a comparison).
 *
 * Artifact shape:
 *   { artifact_type: 'compare_table',
 *     cells:   [{manufacturer, name, chemistry}, ...],
 *     metrics: [{name, label, unit}, ...],
 *     values:  [{cell_index, per_metric: [{metric_name, value, formatted, unit, tool_call_id}]}] }
 */
import { DataValueChip } from './DataValueChip.jsx'

function cellLabel(cell) {
  if (!cell) return ''
  return `${cell.manufacturer || ''} ${cell.name || ''}`.trim() || '(cell)'
}

export function CompareTable({ data }) {
  if (!data || !Array.isArray(data.metrics) || !Array.isArray(data.cells)) {
    return null
  }
  const { cells, metrics, values = [] } = data

  // Index per-cell metric values by metric_name for O(1) lookup.
  const byCellMetric = values.map((row) => {
    const map = {}
    for (const pm of row.per_metric || []) map[pm.metric_name] = pm
    return map
  })

  return (
    <figure class="ion-compare-table">
      <table>
        <thead>
          <tr>
            <th class="ion-compare-table__metric-head">Kennwert</th>
            {cells.map((c, i) => (
              <th key={i}>{cellLabel(c)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {metrics.map((m) => (
            <tr key={m.name}>
              <td class="ion-compare-table__metric">
                {m.label}
                {m.unit ? <span class="ion-compare-table__unit"> ({m.unit})</span> : null}
              </td>
              {cells.map((c, ci) => {
                const pm = byCellMetric[ci] && byCellMetric[ci][m.name]
                return (
                  <td key={ci}>
                    {pm && pm.formatted != null ? (
                      <DataValueChip
                        cell={c}
                        metric={{
                          name: m.name,
                          label: m.label,
                          unit: pm.unit || m.unit,
                          condition: pm.condition || m.condition,
                        }}
                        formatted={pm.formatted}
                        provenanceTrail={pm.provenance_trail}
                      />
                    ) : (
                      <span class="ion-compare-table__na">—</span>
                    )}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  )
}
